import {
  GOOGLE_FONT_OPTIONS,
  LOCAL_FONT_OPTIONS,
  STANDARD_FONT_OPTIONS,
  buildFontTokenValue,
  inferFontFallbackCategory,
  isSafeFontFamilyName,
} from '@/lib/embed/fontFamilies';
import { APCA_MIN_LC, getApcaContrast, type SrgbColor } from '@/lib/color/apca';

export type BrandThemeEditorMode = 'simple' | 'advanced';
export type BrandThemeAppearance = 'light' | 'dark';
export type BrandThemeCornerStyle = 'square' | 'soft' | 'rounded' | 'custom';
export type BrandThemeSurfaceTint = 'neutral' | 'subtle' | 'bold';
export type BrandThemeOuterCanvas = 'solid' | 'transparent';
export type BrandThemeFontPairing = 'atrivial' | 'modern' | 'editorial' | 'friendly';

export interface BrandThemeRecipeV2 {
  version: 2;
  editorMode: BrandThemeEditorMode;
  primaryColor: string;
  secondaryColor: string | null;
  outlineColor: string | null;
  successColor: string | null;
  criticalColor: string | null;
  backgroundColor: string | null;
  cornerStyle: BrandThemeCornerStyle;
  customRadiusPx: number | null;
  fontPairing: BrandThemeFontPairing;
  titleFontFamily: string | null;
  bodyFontFamily: string | null;
  surfaceTint: BrandThemeSurfaceTint;
  outerCanvas: BrandThemeOuterCanvas;
}

export const BRAND_THEME_FONT_PAIRINGS: Record<
  BrandThemeFontPairing,
  { title: string; body: string }
> = {
  atrivial: { title: 'Libre Franklin', body: 'Lato' },
  modern: { title: 'Manrope', body: 'Inter' },
  editorial: { title: 'Playfair Display', body: 'Source Sans 3' },
  friendly: { title: 'Poppins', body: 'Nunito Sans' },
};

export const DEFAULT_BRAND_THEME_RECIPE_V2: BrandThemeRecipeV2 = Object.freeze({
  version: 2,
  editorMode: 'simple',
  primaryColor: '#1F2937',
  secondaryColor: null,
  outlineColor: null,
  successColor: null,
  criticalColor: null,
  backgroundColor: null,
  cornerStyle: 'soft',
  customRadiusPx: null,
  fontPairing: 'atrivial',
  titleFontFamily: null,
  bodyFontFamily: null,
  surfaceTint: 'neutral',
  outerCanvas: 'solid',
});

const HEX_COLOR = /^#([0-9a-f]{6})$/i;
const SHORT_HEX_COLOR = /^#([0-9a-f]{3})$/i;
const knownBodyFontFamilies = new Set(
  [...GOOGLE_FONT_OPTIONS, ...LOCAL_FONT_OPTIONS, ...STANDARD_FONT_OPTIONS].map(({ family }) =>
    family.toLowerCase()
  )
);

function canonicalHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  const full = trimmed.match(HEX_COLOR)?.[1];
  if (full) return `#${full.toUpperCase()}`;
  const short = trimmed.match(SHORT_HEX_COLOR)?.[1];
  if (!short) return null;
  return `#${short
    .split('')
    .map((part) => `${part}${part}`)
    .join('')
    .toUpperCase()}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && allowed.includes(value as T) ? (value as T) : fallback;
}

function safeFamily(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed && isSafeFontFamilyName(trimmed) ? trimmed : null;
}

function safeBodyFamily(value: unknown): string | null {
  const family = safeFamily(value);
  return family && knownBodyFontFamilies.has(family.toLowerCase()) ? family : null;
}

/**
 * Normalizes untrusted recipe input to the complete V2 contract. Invalid values
 * fall back independently, so callers never need a second validation layer.
 */
export function sanitizeBrandThemeRecipe(value: unknown): BrandThemeRecipeV2 {
  const input = isRecord(value) ? value : {};
  const editorMode = oneOf(
    input.editorMode,
    ['simple', 'advanced'] as const,
    DEFAULT_BRAND_THEME_RECIPE_V2.editorMode
  );
  const cornerStyle = oneOf(
    input.cornerStyle,
    ['square', 'soft', 'rounded', 'custom'] as const,
    DEFAULT_BRAND_THEME_RECIPE_V2.cornerStyle
  );
  const effectiveCornerStyle =
    editorMode === 'simple' && cornerStyle === 'custom'
      ? DEFAULT_BRAND_THEME_RECIPE_V2.cornerStyle
      : cornerStyle;
  const radius =
    typeof input.customRadiusPx === 'number' && Number.isFinite(input.customRadiusPx)
      ? Math.min(24, Math.max(0, Math.round(input.customRadiusPx)))
      : null;

  return {
    version: 2,
    editorMode,
    primaryColor: canonicalHex(input.primaryColor) ?? DEFAULT_BRAND_THEME_RECIPE_V2.primaryColor,
    secondaryColor: editorMode === 'advanced' ? canonicalHex(input.secondaryColor) : null,
    outlineColor: editorMode === 'advanced' ? canonicalHex(input.outlineColor) : null,
    successColor: editorMode === 'advanced' ? canonicalHex(input.successColor) : null,
    criticalColor: editorMode === 'advanced' ? canonicalHex(input.criticalColor) : null,
    backgroundColor: editorMode === 'advanced' ? canonicalHex(input.backgroundColor) : null,
    cornerStyle: effectiveCornerStyle,
    customRadiusPx:
      editorMode === 'advanced' && effectiveCornerStyle === 'custom' ? (radius ?? 8) : null,
    fontPairing: oneOf(
      input.fontPairing,
      Object.keys(BRAND_THEME_FONT_PAIRINGS) as BrandThemeFontPairing[],
      DEFAULT_BRAND_THEME_RECIPE_V2.fontPairing
    ),
    titleFontFamily: editorMode === 'advanced' ? safeFamily(input.titleFontFamily) : null,
    bodyFontFamily: editorMode === 'advanced' ? safeBodyFamily(input.bodyFontFamily) : null,
    surfaceTint:
      editorMode === 'advanced'
        ? oneOf(
            input.surfaceTint,
            ['neutral', 'subtle', 'bold'] as const,
            DEFAULT_BRAND_THEME_RECIPE_V2.surfaceTint
          )
        : DEFAULT_BRAND_THEME_RECIPE_V2.surfaceTint,
    outerCanvas:
      editorMode === 'advanced'
        ? oneOf(
            input.outerCanvas,
            ['solid', 'transparent'] as const,
            DEFAULT_BRAND_THEME_RECIPE_V2.outerCanvas
          )
        : DEFAULT_BRAND_THEME_RECIPE_V2.outerCanvas,
  };
}

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  return [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16)) as Rgb;
}

function hexToSrgbColor(hex: string): SrgbColor {
  const [r, g, b] = hexToRgb(hex);
  return { r, g, b };
}

function rgbToHex(rgb: Rgb): string {
  return `#${rgb
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
}

function mix(first: string, second: string, secondWeight: number): string {
  const a = hexToRgb(first);
  const b = hexToRgb(second);
  return rgbToHex(
    a.map((channel, index) => channel * (1 - secondWeight) + b[index] * secondWeight) as Rgb
  );
}

function absoluteApcaContrast(foreground: string, background: string): number {
  return Math.abs(getApcaContrast(hexToSrgbColor(foreground), hexToSrgbColor(background)));
}

function selectReadableTextByApca(backgrounds: string[]): string {
  const candidates = ['#111318', '#ffffff'];
  const scored = candidates.map((candidate) => ({
    candidate,
    minimum: Math.min(
      ...backgrounds.map((background) => absoluteApcaContrast(candidate, background))
    ),
  }));
  return scored.sort((a, b) => b.minimum - a.minimum)[0].candidate;
}

/** Darken/lighten `color` until it meets `minimumLc` APCA contrast on `background`. */
export function ensureApcaContrast(color: string, background: string, minimumLc: number): string {
  if (absoluteApcaContrast(color, background) >= minimumLc) return color.toLowerCase();

  const targets = ['#000000', '#ffffff'];
  let best = color;
  let bestContrast = absoluteApcaContrast(color, background);
  for (let step = 1; step <= 20; step += 1) {
    const candidates = targets.map((target) => mix(color, target, step / 20));
    for (const candidate of candidates) {
      const contrast = absoluteApcaContrast(candidate, background);
      if (contrast > bestContrast) {
        best = candidate;
        bestContrast = contrast;
      }
    }
    const valid = candidates.filter(
      (candidate) => absoluteApcaContrast(candidate, background) >= minimumLc
    );
    if (valid.length > 0) {
      return valid.sort(
        (first, second) =>
          absoluteApcaContrast(second, background) - absoluteApcaContrast(first, background)
      )[0];
    }
  }
  return best;
}

function ensureApcaContrastAcrossSurfaces(
  color: string,
  backgrounds: string[],
  minimumLc: number
): string {
  const minimumContrast = (candidate: string) =>
    Math.min(...backgrounds.map((background) => absoluteApcaContrast(candidate, background)));
  if (minimumContrast(color) >= minimumLc) return color;

  let best = color;
  let bestContrast = minimumContrast(color);
  const targets = ['#000000', '#ffffff'];
  for (let step = 1; step <= 20; step += 1) {
    const candidates = targets.map((target) => mix(color, target, step / 20));
    for (const candidate of candidates) {
      const contrast = minimumContrast(candidate);
      if (contrast > bestContrast) {
        best = candidate;
        bestContrast = contrast;
      }
    }
    const valid = candidates.filter((candidate) => minimumContrast(candidate) >= minimumLc);
    if (valid.length > 0) {
      return valid.sort((first, second) => minimumContrast(second) - minimumContrast(first))[0];
    }
  }
  return best;
}

function ensureSurfaceContrast(background: string, foreground: string, minimumLc: number): string {
  if (absoluteApcaContrast(foreground, background) >= minimumLc) return background;

  const contrastTarget = foreground === '#ffffff' ? '#000000' : '#ffffff';
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mix(background, contrastTarget, step / 20);
    if (absoluteApcaContrast(foreground, candidate) >= minimumLc) return candidate;
  }
  return contrastTarget;
}

const RESOLVED_TOKEN_KEYS = [
  '--embed-outer-canvas',
  '--bg-primary',
  '--bg-secondary',
  '--bg-raised',
  '--bg-raised-hover',
  '--bg-pressed',
  '--bg-subtle',
  '--bg-gradient',
  '--bg-noise',
  '--glyph-primary',
  '--glyph-secondary',
  '--glyph-disabled',
  '--glyph-inverted',
  '--glyph-success',
  '--glyph-critical',
  '--accent-primary',
  '--accent-highlight',
  '--outline-default',
  '--outline-hover',
  '--outline-subtle',
  '--radius-md',
  '--radius-lg',
  '--radius-full',
  '--font-family-title',
  '--font-family-body',
  '--answer-rest-surface',
  '--answer-sibling-surface',
  '--answer-target-surface',
] as const;

export type BrandThemeResolvedToken = (typeof RESOLVED_TOKEN_KEYS)[number];
export type BrandThemeResolvedTokens = Record<BrandThemeResolvedToken, string>;

function resolvedRadius(theme: BrandThemeRecipeV2): number {
  switch (theme.cornerStyle) {
    case 'square':
      return 0;
    case 'rounded':
      return 16;
    case 'custom':
      return theme.customRadiusPx ?? 8;
    case 'soft':
    default:
      return 8;
  }
}

function resolveFontTokens(theme: BrandThemeRecipeV2): {
  title: string;
  body: string;
} {
  const pairing = BRAND_THEME_FONT_PAIRINGS[theme.fontPairing];
  const title = theme.titleFontFamily ?? pairing.title;
  const body = theme.bodyFontFamily ?? pairing.body;
  return {
    title: buildFontTokenValue(title, inferFontFallbackCategory(title)),
    body: buildFontTokenValue(body, inferFontFallbackCategory(body)),
  };
}

/** Resolves the only CSS tokens that website embed branding may apply. */
export function resolveBrandThemeTokens(
  value: BrandThemeRecipeV2,
  appearance: BrandThemeAppearance
): BrandThemeResolvedTokens {
  const theme = sanitizeBrandThemeRecipe(value);
  const surfaceBrand = theme.secondaryColor ?? theme.backgroundColor ?? theme.primaryColor;
  const hasExplicitSurface = theme.secondaryColor !== null;
  const explicitSurfaceTintWeight = { neutral: 0.06, subtle: 0.09, bold: 0.12 }[theme.surfaceTint];
  const siblingTintWeight = hasExplicitSurface ? explicitSurfaceTintWeight * 0.65 : 0.015;
  const raisedTintWeight = hasExplicitSurface ? explicitSurfaceTintWeight * 0.8 : 0;
  const targetTintWeight = hasExplicitSurface ? explicitSurfaceTintWeight + 0.06 : 0.14;
  const canvasTintWeight = 0.025;
  const dark = appearance === 'dark';
  const canvasBase = dark ? '#12151b' : '#fafafa';
  const raisedBase = dark ? '#242832' : '#ffffff';
  const canvas =
    theme.backgroundColor ??
    mix(canvasBase, theme.primaryColor, dark ? canvasTintWeight * 0.5 : canvasTintWeight * 0.35);
  const primaryText = selectReadableTextByApca([canvas]);
  const sibling = ensureSurfaceContrast(
    mix(dark ? '#191d24' : '#f4f4f5', surfaceBrand, siblingTintWeight),
    primaryText,
    APCA_MIN_LC.bodyText
  );
  const raised = ensureSurfaceContrast(
    mix(raisedBase, surfaceBrand, raisedTintWeight),
    primaryText,
    APCA_MIN_LC.bodyText
  );
  const target = ensureSurfaceContrast(
    mix(dark ? '#343a46' : '#e9edf2', surfaceBrand, targetTintWeight),
    primaryText,
    APCA_MIN_LC.bodyText
  );
  const surfaces = [canvas, sibling, raised, target];
  const secondaryText = ensureApcaContrastAcrossSurfaces(
    mix(primaryText, canvas, dark ? 0.28 : 0.2),
    surfaces,
    APCA_MIN_LC.contentText
  );
  const disabledText = ensureApcaContrastAcrossSurfaces(
    mix(primaryText, canvas, dark ? 0.36 : 0.28),
    surfaces,
    APCA_MIN_LC.spotText
  );
  const accent = ensureApcaContrast(theme.primaryColor, canvas, APCA_MIN_LC.spotText);
  const onAccent = selectReadableTextByApca([accent]);
  const outline =
    theme.editorMode === 'simple'
      ? theme.primaryColor
      : (theme.outlineColor ??
        ensureApcaContrast(mix(surfaceBrand, primaryText, 0.45), canvas, APCA_MIN_LC.spotText));
  const outlineHover = ensureApcaContrast(accent, canvas, APCA_MIN_LC.spotText);
  const radius = resolvedRadius(theme);
  const fonts = resolveFontTokens(theme);

  return {
    '--embed-outer-canvas': theme.outerCanvas === 'transparent' ? 'transparent' : canvas,
    '--bg-primary': canvas,
    '--bg-secondary': sibling,
    '--bg-raised': raised,
    '--bg-raised-hover': target,
    '--bg-pressed': mix(target, primaryText, 0.04),
    '--bg-subtle': sibling,
    '--bg-gradient':
      theme.outerCanvas === 'transparent' || theme.backgroundColor
        ? 'none'
        : `linear-gradient(to bottom, transparent 0%, ${sibling} 100%)`,
    '--bg-noise': 'none',
    '--glyph-primary': primaryText,
    '--glyph-secondary': secondaryText,
    '--glyph-disabled': disabledText,
    '--glyph-inverted': onAccent,
    '--glyph-success': theme.successColor ?? (dark ? '#67d6a2' : '#187a4d'),
    '--glyph-critical': theme.criticalColor ?? (dark ? '#ff8d82' : '#b42318'),
    '--accent-primary': accent,
    '--accent-highlight': ensureApcaContrast(
      mix(accent, primaryText, 0.18),
      canvas,
      APCA_MIN_LC.spotText
    ),
    '--outline-default': outline,
    '--outline-hover': outlineHover,
    '--outline-subtle': mix(outline, canvas, 0.45),
    '--radius-md': `${radius}px`,
    '--radius-lg': `${radius}px`,
    '--radius-full': '9999px',
    '--font-family-title': fonts.title,
    '--font-family-body': fonts.body,
    '--answer-rest-surface': raised,
    '--answer-sibling-surface': canvas,
    '--answer-target-surface': raised,
  };
}

export function applyBrandThemeTokensToRoot(tokens: BrandThemeResolvedTokens): void {
  if (typeof document === 'undefined') return;
  for (const [key, value] of Object.entries(tokens)) {
    document.documentElement.style.setProperty(key, value);
  }
}

export function clearBrandThemeTokensFromRoot(): void {
  if (typeof document === 'undefined') return;
  for (const key of RESOLVED_TOKEN_KEYS) {
    document.documentElement.style.removeProperty(key);
  }
}
